import AssistChip from 'LuxKit/Chips/AssistChip'
import LineRefreshIcon from 'LuxKit/Icons/line/LineRefreshIcon'
import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { CompactCardAndPinLayout } from 'tripPlanner/components/CompactItemCard/common'
import { DetailCardAndPinLayout, TransportCardLayout } from 'tripPlanner/components/DetailItemCard/common/DetailCardAndPinLayout'
import RegenerateItemModal from 'tripPlanner/components/Itinerary/RegenerateIndividualItem/RegenerateItemModal'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import useDesktopWithoutTouch from 'tripPlanner/hooks/useDesktopWithoutTouch'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { canSetItemStartDateFromNoDates } from 'tripPlanner/utils/itinerary'

const OverlayHoverOnlyWrapper = styled.div`
  position: absolute;
  top: ${rem(-16)};
  left: 10%;
  transform: translateX(-10%);
  z-index: 1000;
  pointer-events: none;
  display: none;

  > * {
    pointer-events: auto;
  }

  @container (min-width: 640px) {
    &.showOnlyOnHover {
      ${DetailCardAndPinLayout.CardContainer}:hover &,
      ${CompactCardAndPinLayout.CardContainer}:hover &,
      ${TransportCardLayout}:hover & {
        display: inline-flex;
      }
    }
  }
`

interface Props {
  item: TripItem
}

export default function HoverOnlyRegenerateItemButton({
  item,
}: Props) {
  const openModal = useOpenModal()
  const isDesktopWithoutTouch = useDesktopWithoutTouch()
  const intl = useIntl()
  const replaceLabel = intl.formatMessage({
    id: 'vLrybDo4gKDjm_SF70QT',
    defaultMessage: 'Replace',
    description: 'Tooltip and chip label to replace an itinerary item with a new suggestion',
  })

  const showRegenerateItemButton =
    ('name' in item && item.name) ||
    (
      'place' in item &&
      item.place &&
      'name' in item.place &&
      item.place.name
    )

  const canSetDatesFromNoDates = canSetItemStartDateFromNoDates(item)

  const shouldShowReplace = showRegenerateItemButton && !item.isSuggested && item.startDate && canSetDatesFromNoDates

  const handleReplaceClick = useCallback(() => {
    openModal(RegenerateItemModal, { item }, { size: 'L', height: 'full', tabletHeight: 'auto' })
  }, [openModal, item])

  if (!isDesktopWithoutTouch || !shouldShowReplace) {
    return null
  }

  return (
    <OverlayHoverOnlyWrapper className="showOnlyOnHover">
      <AssistChip
        size="small"
        variant="filled"
        startIcon={<LineRefreshIcon size="S" />}
        onClick={handleReplaceClick}
        title={replaceLabel}
      >
        {replaceLabel}
      </AssistChip>
    </OverlayHoverOnlyWrapper>
  )
}
